/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';

// Component imports
import { Button, Checkbox, Col, Divider, Empty, Form, Input, Row, Select, Skeleton, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';
import LLMConfigurationFieldGroup from '../../../Forms/Fields/LLMConfigurationFieldGroup/LLMConfigurationFieldGroup';
import CustomInputNumber from '../../../Forms/Fields/CustomInputNumber';

// Misc imports
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { VARIABLE_TYPES } from '../../../../constants/main';
import { useFetchBotQuery } from '../../../../store/bots/botsApi';
import { NAV_BOT } from '../../../../constants/navigation';
import replaceUrlParams from '../../../../utils/string/replaceUrlParams';
import { INTEGRATIONS, PLUGIN_CATEGORY } from '../../../../constants/integrations';
import { LLM_RESPONSE_FORMAT } from '../../../../constants/llmConfiguration';

// Constant declarations
const { Text } = Typography;

const GenerationNodeForm = ({ form, setCanSubmit }) => {
  const navigate = useNavigate();
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();

  const { data: bot, isFetching: isFetchingBot } = useFetchBotQuery({ orgId, tenantId });
  const { data: storyVariables, isFetching: isFetchingStoryVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });

  const [localVariables, setLocalVariables] = useState([]);

  const selectedIntegration = Form.useWatch(['data', 'generationParams', 'provider'], form);
  const responseTypeValue = Form.useWatch(['data', 'generationParams', 'configuration', 'responseType'], form);

  const isResponseInJson = useMemo(() => {
    return responseTypeValue === LLM_RESPONSE_FORMAT.JSON.value;
  }, [responseTypeValue]);

  // Whenever the story variables change, update the local variables
  useEffect(() => {
    const validVariables = [...storyVariables.filter(variable => variable.type !== VARIABLE_TYPES.STATIC)];
    setLocalVariables(validVariables);
  }, [storyVariables]);

  const updateVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setLocalVariables([...new Set([...storyVariables.filter(variable => variable.type !== VARIABLE_TYPES.STATIC), { name: newVariableName, type: '' }])]);
      form.setFieldsValue({ data: { variable: { key: newVariableName } } });
      form.validateFields([{ data: { variable: { key: newVariableName } } }]);
    }
  };

  return (
    <Fragment>
      {
        isFetchingBot ? (
          <Skeleton active />
        ) : bot?.plugins?.filter((plugin) => plugin.category === PLUGIN_CATEGORY.LLM_NODE).length > 0 ? (
          <Fragment>

            <Row gutter={16} className="mb-4">
              <Col xs={24}>
                <Form.Item
                  className="mb-1"
                  label= {t('Friendly name')}
                  name={['data', 'friendlyName']}
                >
                  <Input
                    placeholder={t('Enter a short friendly name')}
                    size="large"
                    maxLength={30}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left" className="mb-6 mt-0">
              <Text type="secondary" className="fs-base">{t('LLM Configuration')}</Text>
            </Divider>

            <Row gutter={16} className="mb-4">
              <Col xs={24}>
                <Form.Item
                  label={t('LLM Plugin')}
                  name={['data', 'generationParams', 'provider']}
                  rules={[
                    {
                      required: true,
                      message: t('Provider cannot be blank.')
                    }]}
                >
                  <Select
                    placeholder={t('Select an LLM provider')}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                    options={bot?.plugins.filter((plugin) => plugin.category === 'LLM_NODE').map((llm) => {
                      return {
                        value: llm.integration.type,
                        label: t( Object.values(INTEGRATIONS).find(integration => integration.type === llm.integration.type).type
                          , { context: 'type' })
                      };
                    })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <LLMConfigurationFieldGroup
                  form={form}
                  integrationProviderType={selectedIntegration}
                  setCanSubmit={setCanSubmit}
                  basePath={['data', 'generationParams']}
                  shouldValidatePromptJson
                />
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'generationParams', 'includeHistory']}
                  valuePropName="checked"
                  className="mb-2"
                >
                  <Checkbox>
                    <Text>{t('Include history')}</Text>
                    <Tooltip title={t('The LLM will include the session history when generating the response.')}>
                      <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'generationParams', 'maxHistoryTurns']}
                  label={
                    <Fragment>
                      <Text>{t('Maximum history turns')}</Text>
                      <Tooltip title={t('A turn is a group of messages starting with one or more consecutive user messages followed by any agent responses until the next user message.')}>
                        <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                      </Tooltip>
                    </Fragment>
                  }
                  validateTrigger={['onChange', 'onBlur']}
                  className="mb-0"
                >
                  <CustomInputNumber
                    className="w-10"
                    disabled={!form.getFieldValue(['data', 'generationParams', 'includeHistory'])}
                    type="number"
                    min={1}
                    max={30}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" className="my-6">
              <Text type="secondary" className="fs-base">{t('LLM Response')}</Text>
              <Tooltip title={t('Configure how the LLM response should be used.')}>
                <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
              </Tooltip>

            </Divider>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'generationParams', 'variable']}
                  label={
                    <Row>
                      <Col span={24}>
                        <Text>
                          {t('Store response as a variable')}
                        </Text>
                        <Tooltip title={t('Save the LLM\'s response as a variable (value) for use in message templates or control flow nodes.')}>
                          <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                        </Tooltip>

                      </Col>
                      <Col span={24} className="mb-2">
                        <Text type="secondary" className="fs-sm mr-3">
                          {t('Select a variable (name) or type to create custom.')}
                        </Text>
                      </Col>
                    </Row>
                  }
                  rules={[
                    {
                      pattern: /^[a-zA-Z0-9_$\\.]*$/,
                      message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
                    }]
                  }
                >
                  {
                    storyVariables && localVariables ? (
                      <Select
                        showSearch
                        className="w-100"
                        placeholder={t('Type / select variable')}
                        // When the user types a new variable name, add it to the list of local variables
                        onSearch={updateVariables}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                        onBlur={(e) => updateVariables(e.target.value)}
                        loading={isFetchingStoryVariables}
                        options={(localVariables || [])
                          .filter((variable) => {
                            return ![VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.LIVECHAT, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.CSAT].includes(variable.type);
                          })
                          .map((element) => {
                            return {
                              value: element.name,
                              label: element.name
                            };
                          })
                        // Keep only unique values
                          .filter((elem, index, self) => self.findIndex((t) => {
                            return (t.value === elem.value);
                          }) === index)
                        }
                      />
                    ) : null
                  }
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={['data', 'generationParams', 'responseStatusCodeVariableName']}
                  label={
                    <Row>
                      <Col span={24}>
                        <Text>
                          {t('Store response status code as a variable')}
                        </Text>
                        <Tooltip title={t('Save the LLM\'s response status code as a variable (value) for use in message templates or control flow nodes.')}>
                          <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                        </Tooltip>

                      </Col>
                      <Col span={24} className="mb-2">
                        <Text type="secondary" className="fs-sm mr-3">
                          {t('Select a variable (name) or type to create custom.')}
                        </Text>
                      </Col>
                    </Row>
                  }
                  rules={[
                    {
                      pattern: /^[a-zA-Z0-9_$\\.]*$/,
                      message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
                    }]
                  }
                >
                  {
                    storyVariables && localVariables ? (
                      <Select
                        showSearch
                        className="w-100"
                        placeholder={t('Type / select variable')}
                        // When the user types a new variable name, add it to the list of local variables
                        onSearch={updateVariables}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                        onBlur={(e) => updateVariables(e.target.value)}
                        loading={isFetchingStoryVariables}
                        options={(localVariables || [])
                          .filter((variable) => {
                            return ![VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.LIVECHAT, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.CSAT].includes(variable.type);
                          })
                          .map((element) => {
                            return {
                              value: element.name,
                              label: element.name
                            };
                          })
                        // Keep only unique values
                          .filter((elem, index, self) => self.findIndex((t) => {
                            return (t.value === elem.value);
                          }) === index)
                        }
                      />
                    ) : null
                  }
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={['data', 'generationParams', 'sendResponse']}
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox>
                    <Text>{t('Send LLM Response to user.')}</Text>
                    <Tooltip title={t('The agent will directly present the LLM\'s response to the user.')}>
                      <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'generationParams', 'showTypingIndicator']}
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox
                    defaultChecked={true}
                  >
                    <Text>{t('Show Typing Indicator')}</Text>
                    <Tooltip title={t('Shows a typing indicator to the end-user while the agent is generating a response.')}>
                      <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                {
                  isResponseInJson
                    ? <Form.Item
                      shouldUpdate
                      label={t('Extract Parameters')}
                      className="my-4"
                      tooltip={{
                        icon: <QuestionCircleOutlined />,
                        title: t('Specify the parameters to be extracted from the LLM\'s response.')
                      }}
                    >
                      <Form.List
                        name={['data', 'generationParams', 'extractParameters']}
                      >
                        {(fields, { add, remove }) => (
                          <Fragment>
                            {fields.map((field) => {
                              return (
                                <Row gutter={16} >
                                  <Col flex="auto">
                                    <Form.Item
                                      label={t('JSON Property')}
                                      name={[field.name, 'key']}
                                      tooltip={{
                                        icon: <QuestionCircleOutlined />,
                                        title: t('Enter a property found in the top-level of the JSON response from the LLM.')
                                      }}
                                    >
                                      <Input placeholder={t('Select Data')} />
                                    </Form.Item>
                                  </Col>

                                  <Col flex="auto">
                                    <Form.Item
                                      label={t('Variable Name')}
                                      name={[field.name, 'value']}
                                      tooltip={{
                                        icon: <QuestionCircleOutlined />,
                                        title: t('Enter the variable name to store the extracted JSON property.')
                                      }}
                                    >
                                      {
                                        storyVariables && localVariables ? (
                                          <Select
                                            showSearch
                                            className="w-90"
                                            placeholder={t('Type / select variable')}
                                            // When the user types a new variable name, add it to the list of local variables
                                            onSearch={updateVariables}
                                            getPopupContainer={triggerNode => triggerNode.parentElement}
                                            onBlur={(e) => updateVariables(e.target.value)}
                                            loading={isFetchingStoryVariables}
                                            options={(localVariables || [])
                                              .filter((variable) => {
                                                return ![VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.LIVECHAT, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.CSAT].includes(variable.type);
                                              })
                                              .map((element) => {
                                                return {
                                                  value: element.name,
                                                  label: element.name
                                                };
                                              })
                                            // Keep only unique values
                                              .filter((elem, index, self) => self.findIndex((t) => {
                                                return (t.value === elem.value);
                                              }) === index)
                                            }
                                          />
                                        ) : null
                                      }
                                    </Form.Item>
                                  </Col>

                                  <Col flex="none">
                                    <Button type="text" onClick={() => remove(field.name)} icon={<DeleteIcon />} />
                                  </Col>
                                </Row>
                              );
                            })}

                            <Button type="dashed" onClick={() => add()} icon={<PlusIcon />}>
                              {t('Add Parameter')}
                            </Button>
                          </Fragment>
                        )}
                      </Form.List>
                    </Form.Item> : null
                }
              </Col>
            </Row>
          </Fragment>
        ) : (
          <Empty
            description={
              <Text type="secondary">
                {t('No plugin enabled. Please')}
                <Button
                  type="link"
                  className="px-1"
                  onClick={() => {
                    navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: tenantId })}?view=plugins&scope=LLM`);
                  }}
                >
                  {t('visit the plugins page')}
                </Button>
                {t('to enable a new one.')}
              </Text>}
          />
        )
      }
    </Fragment>
  );
};

GenerationNodeForm.propTypes = {
  form: PropTypes.object,
  setCanSubmit: PropTypes.func.isRequired
};

export default GenerationNodeForm;
